// src/pages/PublicProfilePage.js — avec OG meta tags
import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../lib/firebase";
import { computeLevel, ONI_FORMS } from "../data/demons";
import DemonCard from "../components/DemonCard";
import { ChronicleCard } from "./JournalPage";
import OniCard from "../components/OniCard";
import "./PublicProfilePage.css";

const API = process.env.REACT_APP_API_URL || "http://localhost:3001";

// Injecte l'image fraîche depuis ONI_FORMS (même logique que useAuth)
function enrichDemon(demon) {
  if (!demon?.oni?.id) return demon;
  const fresh = ONI_FORMS.find(o => o.id === demon.oni.id);
  if (!fresh) return demon;
  return { ...demon, oni: { ...fresh, ...demon.oni, image: fresh.image, emoji: fresh.emoji } };
}

// Injecte / met à jour les meta OG dans le <head>
function setMeta(property, content) {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function injectOGTags({ uid, displayName, oniName, oniEmoji, tagline, level }) {
  const siteUrl   = window.location.origin;
  const profileUrl = `${siteUrl}/u/${uid}`;
  const ogImageUrl = `${API}/og/profile/${uid}`;
  const title     = `${oniEmoji} ${displayName} — ${oniName} · Takkyu Oni`;
  const description = `${tagline} · LV.${level} · Vois son profil démoniaque sur Takkyu Oni.`;

  document.title = title;
  setMeta("og:title",       title);
  setMeta("og:description", description);
  setMeta("og:image",       ogImageUrl);
  setMeta("og:url",         profileUrl);
  setMeta("og:type",        "profile");
  setMeta("og:site_name",   "Takkyu Oni");
  // Twitter card
  setMeta("twitter:card",        "summary_large_image");
  setMeta("twitter:title",       title);
  setMeta("twitter:description", description);
  setMeta("twitter:image",       ogImageUrl);
}

// Restore default OG on unmount
function restoreDefaultOG() {
  document.title = "TAKKYU ONI — 卓球鬼";
  const defaults = {
    "og:title":       "TAKKYU ONI — Awaken Your Demon",
    "og:description": "Life wear pour les démons du ping. Crée ton profil démoniaque.",
    "og:image":       `${window.location.origin}/og-default.png`,
    "og:url":         window.location.origin,
    "twitter:card":   "summary_large_image",
  };
  Object.entries(defaults).forEach(([p,c]) => setMeta(p, c));
}

export default function PublicProfilePage() {
  const { uid }  = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied]  = useState(false);
  const [showOniCard, setShowOniCard] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const snap = await getDoc(doc(db, "users", uid));
        if (!snap.exists() || snap.data().profilePublic === false) {
          setNotFound(true); setLoading(false); return;
        }
        const data = snap.data();
        setProfile({ ...data, demon: enrichDemon(data.demon) });

        // Inject OG tags
        const level = computeLevel(data.xp || 0);
        injectOGTags({
          uid,
          displayName: data.displayName || "Oni Inconnu",
          oniName:     data.demon?.oni?.name || "Oni",
          oniEmoji:    data.demon?.oni?.emoji || "👹",
          tagline:     data.demon?.oni?.tagline || "",
          level:       level.current.level,
        });
      } catch(e) { setNotFound(true); }
      setLoading(false);
    }
    load();
    return () => restoreDefaultOG(); // cleanup on unmount
  }, [uid]);

  const copyLink = () => {
    navigator.clipboard?.writeText(window.location.href).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    });
  };

  if (loading) return (
    <div style={{display:"flex", alignItems:"center", justifyContent:"center", height:"100vh"}}>
      <span className="spinner" style={{width:24, height:24, borderWidth:3}}/>
    </div>
  );

  if (notFound || !profile) return (
    <div className="pub-page section" style={{paddingTop:"8rem", textAlign:"center"}}>
      <span style={{fontSize:"3rem"}}>🌑</span>
      <h2 className="section-title" style={{marginTop:"1rem"}}>Ce démon est dans l'ombre.</h2>
      <p style={{color:"var(--ash)", marginTop:".75rem", fontSize:".83rem"}}>Profil privé ou introuvable.</p>
      <Link to="/" className="btn btn-ghost btn-sm" style={{marginTop:"2rem"}}>← RETOUR</Link>
    </div>
  );

  const demon    = profile.demon;
  const xp       = profile.xp || 0;
  const levelInfo = computeLevel(xp);
  const journal  = (profile.journal || []).filter(e => e.title);
  const badges   = profile.badges || [];

  return (
    <>
    <div className="pub-page">
      {/* OG Image preview hint (mobile) */}
      <div className="pub-og-hint">
        <img
          src={`${API}/og/profile/${uid}`}
          alt="Profil démoniaque"
          className="pub-og-preview"
          onError={e => e.target.style.display="none"}
        />
      </div>

      <div className="section" style={{paddingTop:"7rem"}}>
        <div className="pub-header">
          <div className="pub-identity">
            <span className="section-eyebrow">鬼 · Profil Public</span>
            <h1 className="section-title">{profile.displayName || "Oni Inconnu"}</h1>
            <div className="pub-meta">
              <span className="badge badge-blood">{levelInfo.current.badge} LV.{levelInfo.current.level}</span>
              <span className="badge badge-ghost">{levelInfo.current.title}</span>
              <span className="badge badge-ember">{xp} XP</span>
              {badges.length > 0 && (
                <span className="badge badge-gold">{badges.length} badge{badges.length>1?"s":""}</span>
              )}
            </div>
          </div>
          <div className="pub-actions" style={{display:"flex",gap:"8px"}}>
            <button className="btn btn-ghost btn-sm" onClick={copyLink}>
              {copied ? "✓ COPIÉ" : "COPIER LE LIEN ↗"}
            </button>
            {demon && (
              <button className="btn btn-ghost btn-sm" onClick={() => setShowOniCard(true)}>
                📸 CARTE ONI
              </button>
            )}
          </div>
        </div>
        <div className="divider"/>

        <div className="pub-grid">
          {demon ? (
            <div className="pub-demon">
              <DemonCard demon={demon} xp={xp} />
            </div>
          ) : (
            <div className="pub-no-demon">
              <span>🌑</span>
              <p>Démon pas encore apparu.</p>
            </div>
          )}

          <div className="pub-journal">
            <p className="pub-section-title">CHRONIQUES · {journal.length} ENTRÉE{journal.length!==1?"S":""}</p>
            {journal.length === 0 ? (
              <p className="pub-empty">Aucune chronique publique.</p>
            ) : (
              <div className="pub-chronicles">
                {journal.slice(0, 20).map((entry, i) => (
                  <ChronicleCard key={entry.id||i} entry={entry} compact />
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="pub-share-banner">
          <p>Crée ton propre démon sur <strong>Takkyu Oni</strong></p>
          <Link to="/awaken" className="btn btn-primary btn-sm">RÉVEILLER MON DÉMON →</Link>
        </div>
      </div>
    </div>
    {showOniCard && demon && (
      <OniCard
        demon={demon}
        xp={xp}
        displayName={profile.displayName}
        onClose={() => setShowOniCard(false)}
      />
    )}
    </>
  );
}
